import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

interface FilterChipListProps {
    items: string[];
    activeItems: string[];
    onItemClick: (e: React.MouseEvent, item: string) => void;
    emptyLabel: string;
    collapsedCount?: number;
    forceExpanded?: boolean;
    noMatchLabel?: string;
}

/**
 * FilterChipList — Reusable component for rendering a list of filter chips
 * with optional show-more/show-less collapse.
 *
 * - Renders chips in alphabetical order (via useMemo, stable across renders)
 * - Collapses to first N chips by default; expands on "Show more" click
 * - Each chip is a <button> (accessible, keyboard-focusable)
 * - Chips wrap naturally with no overflow
 */
export default function FilterChipList({
    items,
    activeItems,
    onItemClick,
    emptyLabel,
    collapsedCount = 8,
    forceExpanded = false,
    noMatchLabel,
}: FilterChipListProps) {
    const { t } = useLanguage();
    const [expanded, setExpanded] = useState(false);

    // Memoize sorted items to avoid re-sorting on every render
    const sortedItems = useMemo(
        () => [...items].sort((a, b) => a.localeCompare(b)),
        [items]
    );

    // Determine which items to display
    const isExpanded = forceExpanded || expanded;
    const displayedItems = isExpanded ? sortedItems : sortedItems.slice(0, collapsedCount);
    const hasMore = sortedItems.length > collapsedCount;
    const hiddenCount = sortedItems.length - collapsedCount;

    if (sortedItems.length === 0) {
        const messageToShow = noMatchLabel || emptyLabel;
        return (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', padding: '0.5rem 0' }}>
                {messageToShow}
            </div>
        );
    }

    return (
        <div>
            <div>
                {displayedItems.map((item) => (
                    <button
                        key={item}
                        type="button"
                        className={`filter-chip ${activeItems.includes(item) ? 'active' : ''}`}
                        onClick={(e) => onItemClick(e, item)}
                        aria-pressed={activeItems.includes(item)}
                    >
                        {item}
                    </button>
                ))}
            </div>

            {/* Show more / Show less toggle (hidden if forceExpanded) */}
            {hasMore && !forceExpanded && (
                <button
                    type="button"
                    className="filter-chip-toggle"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    aria-label={expanded ? t('sidebar.showLess') : t('sidebar.showMore')}
                >
                    <span style={{ marginRight: '0.25rem', display: 'inline-flex', alignItems: 'center' }}>
                        {expanded ? (
                            <ChevronUp size={14} strokeWidth={2} />
                        ) : (
                            <ChevronDown size={14} strokeWidth={2} />
                        )}
                    </span>
                    {expanded
                        ? t('sidebar.showLess')
                        : `${t('sidebar.showMore')} (+${hiddenCount})`}
                </button>
            )}
        </div>
    );
}
